import { BasicAuthServiceHelper } from '../Auth'
import { CreateSetupIntent, CreateSubscription, GetPK, SendToken } from './apiTypes'
import { PaymentIntentResult } from '@stripe/stripe-js'
import { APIError } from '@/data'
import { SendTokenStatus } from '@/modules/Stripe/apiTypes/base'

export const getStripeApiKey = async (plan?: string) => {
  const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    GetPK.Response,
    never,
    never,
    GetPK.SearchParams
  >(GetPK.Route, { searchParams: plan ? { plan } : {} })
  return response.key
}

export const sendStripeToken = async (
  params: {
    token: string
    planId: string
    coupon?: string
  },
  confirm: (paymentIntentClientSecret: string) => Promise<PaymentIntentResult>,
): Promise<void> => {
  const { token, planId, coupon } = params

  const { id: paymentIntentClientSecret, status: checkoutStatus } =
    await BasicAuthServiceHelper.requestAuthenticatedV2Async<SendToken.Response, SendToken.Body>(SendToken.Route, {
      body: {
        stripeToken: token,
        planID: planId,
        coupon,
      },
    })

  switch (checkoutStatus) {
    case SendTokenStatus.Active:
    case SendTokenStatus.Trialing:
      // Do nothing more here, because the premium status will change with Mercure SSE
      break
    case SendTokenStatus.Incomplete:
      {
        if (!paymentIntentClientSecret) {
          throw new Error('NO paymentIntentClientSecret returned from API')
        }
        // 3rd: Confirm Payment with Stripe SDK
        const { paymentIntent, error: confirmError } = await confirm(paymentIntentClientSecret)

        if (confirmError) {
          throw APIError.fromStripeError(confirmError)
        }

        switch (paymentIntent.status) {
          case 'succeeded':
            // Do nothing more here, because the premium status will change with Mercure SSE
            break
          default:
            throw new Error(`PaymentIntent Status invalid: ${paymentIntent.status}`)
        }
      }
      break
    default:
      throw new Error(`Checkout Status invalid: ${checkoutStatus}`)
  }
}

export const createSetupIntent = async ({ plan, paypal = false }: { plan?: string; paypal?: boolean }) => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    CreateSetupIntent.Response,
    never,
    never,
    CreateSetupIntent.SearchParams
  >(CreateSetupIntent.Route, {
    searchParams: {
      ...(paypal ? { paypal: true } : {}),
      ...(plan ? { plan } : {}),
    },
  })
}

export const createSubscription = async (body: CreateSubscription.Body) => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<CreateSubscription.Response, CreateSubscription.Body>(
    CreateSubscription.Route,
    { body },
  )
}
